// Shopify-specific Vue binder. The shared form state mirrors useSignup; the command itself stays
// in AuthShopifyRegisterFlow so the screen only parses/strips its one-time route handoff.
import { computed, reactive, ref, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  AuthShopifyRegisterFlow,
  isShopifyRegistrationContext,
  type ShopifyRegistrationContext,
} from '../flows/AuthShopifyRegisterFlow'
import { AuthError } from '../models/auth.error'
import type { SignupPayload } from '../models/auth.types'
import type { SignupField } from '../validators/AuthValidator'

type FieldErrors = Partial<Record<SignupField, string>>
const INVALID_CONTEXT_KEY = 'auth.signup.shopifyContextInvalid' as const

export interface UseShopifyRegister {
  user: SignupPayload
  contextValid: ComputedRef<boolean>
  emailExists: Ref<boolean>
  pending: Ref<boolean>
  error: Ref<string | null>
  fieldErrors: Ref<FieldErrors>
  submit: (terms: boolean) => Promise<void>
  signupWithGoogle: () => void
}

export function useShopifyRegister(
  context: Partial<ShopifyRegistrationContext>,
): UseShopifyRegister {
  const router = useRouter()
  const flow = new AuthShopifyRegisterFlow()

  const user = reactive<SignupPayload>({ email: '', password: '', name: '', company_name: '' })
  const contextValid = computed(() => isShopifyRegistrationContext(context))
  const emailExists = ref(false)
  const pending = ref(false)
  const error = ref<string | null>(contextValid.value ? null : INVALID_CONTEXT_KEY)
  const fieldErrors = ref<FieldErrors>({})

  async function submit(terms: boolean): Promise<void> {
    pending.value = true
    error.value = null
    emailExists.value = false
    fieldErrors.value = {}
    try {
      const outcome = await flow.register({ ...user }, terms, context)
      if (!outcome.ok) {
        if ('contextError' in outcome) error.value = outcome.contextError
        else fieldErrors.value = outcome.fieldErrors
        return
      }
      if (!isShopifyRegistrationContext(context)) {
        error.value = INVALID_CONTEXT_KEY
        return
      }
      // The context is opaque/non-secret and is the only Shopify handoff allowed through the MFA
      // leg. Raw provider code/password values are never accepted or copied into this route.
      await router.push({
        name: 'SignUpMfa',
        query: {
          email: outcome.email,
          shop: context.shop.trim(),
          shopify_context: context.shopify_context.trim(),
        },
      })
    } catch (cause) {
      const authError = AuthError.from(cause)
      if (authError.code === 'emailExists') {
        emailExists.value = true
      } else {
        error.value = authError.i18nKey
        for (const [field, messages] of Object.entries(authError.fields)) {
          const first = messages[0]
          if (first) fieldErrors.value[field as SignupField] = first
        }
      }
    } finally {
      pending.value = false
    }
  }

  function signupWithGoogle(): void {
    if (!flow.signupWithGoogle(context)) error.value = INVALID_CONTEXT_KEY
  }

  return {
    user,
    contextValid,
    emailExists,
    pending,
    error,
    fieldErrors,
    submit,
    signupWithGoogle,
  }
}
